import React, {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
} from 'react'
import {
    LocalStorageKeys,
    PAGE_SIZES,
    PageSizeType,
    SetStateFn,
} from '~config/constants'
import {
    ClinicFieldsFragment,
    MessageOrigin,
    NotificationLogMessageFieldsFragment,
    QueryInput,
    useCountMessagesQuery,
    useQueryClinicNotificationLogMessagesQuery,
} from '~graphql/generated/graphql'
import { eqStringFilter } from '~utils/query-helpers'
import { constructOrderBy } from '~utils/querying'
import { useStateWithDebounce } from '~utils/hooks/use-state-with-debounce'
import { usePageControls } from '~utils/hooks/use-page-controls'
import {
    NotificationTemplateValues,
    useNotificationTemplateValues,
} from './notif-templates'
import { ChatFilterValues, useChatFilterValues } from './chat-filter'
import { useLocalStorage } from '~utils/hooks/use-local-storage'
import { getCleanedQueryParams } from '~utils/get-cleaned-query-params'

export function useNotificationLogsContext() {
    return useContext(NotificationLogsContext)
}

interface Props extends PropsWithChildren {
    clinic: ClinicFieldsFragment
}

export default function NotificationLogsProvider({ clinic, children }: Props) {
    const { selectedClient, setSelectedClient, selectedChatId, loadingChat } =
        useChatFilterValues()
    const [hideInactiveNotifications, setHideInactiveNotifications] =
        useLocalStorage(LocalStorageKeys.hideInactiveNotifications, true)
    const { notificationTemplatesLoading, notificationTemplates } =
        useNotificationTemplateValues(clinic, hideInactiveNotifications)
    const initialNotificationTemplateIds = useMemo(
        () => getCleanedQueryParams().notificationTemplateIds || [],
        [],
    )

    const { page, setPage, pageSize, setPageSize } = usePageControls(
        PAGE_SIZES[1],
    )

    const [
        notificationTemplateIds,
        setNotificationTemplateIds,
        debouncedNotificationTemplateIds,
    ] = useStateWithDebounce<string[]>(initialNotificationTemplateIds)

    // Empty selection = all system messages for the clinic (no template filter).
    // When templates are selected, filter via message_notification_templates.
    const notificationTemplateFilter =
        debouncedNotificationTemplateIds.length > 0
            ? debouncedNotificationTemplateIds
            : undefined

    const queryInput: QueryInput[] = useMemo(() => {
        const filters: QueryInput[] = [
            eqStringFilter('message_origin', MessageOrigin.System),
        ]

        if (!!selectedChatId) {
            filters.push(eqStringFilter('chat', selectedChatId))
        }

        return filters
    }, [selectedChatId])

    const skipMessagesQuery = !clinic?.id

    const notificationTemplateIdsKey =
        debouncedNotificationTemplateIds.join(',')

    useEffect(() => {
        setPage(0)
    }, [selectedChatId, notificationTemplateIdsKey, setPage])

    const handleSetPageSize = useCallback(
        (size: PageSizeType) => {
            setPage(0)
            setPageSize(size)
        },
        [setPage, setPageSize],
    )

    const { data: countData } = useCountMessagesQuery({
        variables: {
            clinicId: clinic?.id || '',
            where: queryInput,
            notificationTemplateIds: notificationTemplateFilter,
        },
        skip: skipMessagesQuery,
    })

    const { data, loading: messagesLoading } =
        useQueryClinicNotificationLogMessagesQuery({
            variables: {
                clinicId: clinic?.id || '',
                where: queryInput,
                page,
                pageSize,
                orderBy: constructOrderBy('created_at', false),
                notificationTemplateIds: notificationTemplateFilter,
            },
            skip: skipMessagesQuery,
        })
    const loading = messagesLoading
    const count = countData?.countMessages || 0
    const messages = data?.queryClinicMessages || []
    return (
        <NotificationLogsContext.Provider
            value={{
                count,
                loading,
                messages,

                page,
                setPage,
                pageSize,
                setPageSize: handleSetPageSize,
                notificationTemplateIds,
                setNotificationTemplateIds,
                hideInactiveNotifications,
                setHideInactiveNotifications,

                selectedClient,
                setSelectedClient,
                selectedChatId,

                loadingChat,
                notificationTemplatesLoading,
                notificationTemplates,
            }}
        >
            {children}
        </NotificationLogsContext.Provider>
    )
}

const NotificationLogsContext = createContext<NotificationLogsContextType>(
    {} as NotificationLogsContextType,
)

type NotificationLogsContextType = {
    count: number
    loading: boolean
    messages: NotificationLogMessageFieldsFragment[]

    page: number
    setPage: SetStateFn<number>
    pageSize: number
    setPageSize: (size: PageSizeType) => void
    notificationTemplateIds: string[]
    setNotificationTemplateIds: (ids: string[]) => void
    hideInactiveNotifications: boolean
    setHideInactiveNotifications: (hideInactive: boolean) => void
} & NotificationTemplateValues &
    ChatFilterValues
